import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { toast } from "sonner";
import { CopyButton } from "@/components/animate-ui/components/buttons/copy";
import { TaskConfirmationModal } from "@/components/task-confirmation-modal";
import { RunPreviewPanel } from "@/components/teammate/run-preview-panel";
import { TaskBoard } from "@/components/teammate/task-board";
import { TaskFilters } from "@/components/teammate/task-filters";
import { TaskList } from "@/components/teammate/task-list";
import { TaskRunPanel } from "@/components/teammate/task-run-panel";
import { TeammateHeader } from "@/components/teammate/teammate-header";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
	useAgent,
	useAgentRuns,
	useEnqueueTask,
	useRunListFreshness,
	useRunTask,
	useSchedules,
	useTasks,
	useUpdateTaskById,
} from "@/lib/api/hooks/tasks";
import type { components } from "@/lib/api/types";
import { captureFrontendError } from "@/lib/errors/capture";
import { buildMetaTags } from "@/lib/seo/meta";
import type { WorkflowStage } from "@/lib/task-workflow";
import { useTaskModal } from "@/lib/tasks/hooks/use-task-modal";
import { filterAndSortTasks } from "@/lib/utils/filter-tasks";
import { useTeammateUIStore } from "@/stores/teammate-ui-store";

export const Route = createFileRoute("/_dashboard/teammates/$id")({
	head: () => {
		const meta = buildMetaTags({
			title: "Teammate",
			description: "Manage your teammate and their tasks",
			noIndex: true,
			noFollow: true,
		});

		return { meta };
	},

	component: TeammatePage,
});

function TeammatePage() {
	const { id } = Route.useParams();
	const agentId = Number.parseInt(id, 10);
	const navigate = useNavigate();

	// Fetch the specific agent
	const { data: agent, isLoading: isLoadingAgent, error: agentError } = useAgent(agentId);
	const { data: allTasks, isLoading: isLoadingTasks } = useTasks();
	const { data: schedulesData } = useSchedules();
	const schedules = schedulesData?.schedules || [];
	const { data: agentRuns = [] } = useAgentRuns(agentId);
	useRunListFreshness();

	// Get filter state and panel state
	const {
		searchQuery,
		statusFilter,
		typeFilter,
		sortOption,
		viewMode,
		kanbanHideUnassigned,
		setKanbanHideUnassigned,
		selectedTaskId,
		isPanelOpen,
		setSelectedTask,
		closePanel,
		previewRunId,
		isRunPreviewOpen,
		openRunPreview,
		closeRunPreview,
	} = useTeammateUIStore();

	// Task modal hook — encapsulates all modal state, mutations, and handlers
	const taskModal = useTaskModal({
		agentId,
		agent,
		allTasks,
		schedules,
		navigate,
	});

	// Board-level mutations (not modal-related)
	const runTask = useRunTask();
	const enqueueTask = useEnqueueTask();
	const updateTaskById = useUpdateTaskById();

	// Filter and sort tasks
	const tasks = useMemo(
		() =>
			filterAndSortTasks({
				tasks: allTasks ?? [],
				agentId,
				searchQuery,
				statusFilter,
				typeFilter,
				sortOption,
				schedules,
			}),
		[allTasks, agentId, searchQuery, statusFilter, typeFilter, sortOption, schedules]
	);

	const latestRunByTaskId = useMemo(() => {
		const map = new Map<number, components["schemas"]["RunResponse"]>();
		for (const run of agentRuns ?? []) {
			const taskId = run.task_id;
			if (typeof taskId !== "number") continue;

			const existing = map.get(taskId);
			if (!existing) {
				map.set(taskId, run);
				continue;
			}

			const existingTime = existing.created_at ? new Date(existing.created_at).getTime() : NaN;
			const runTime = run.created_at ? new Date(run.created_at).getTime() : NaN;
			if (!Number.isNaN(runTime) && (Number.isNaN(existingTime) || runTime > existingTime)) {
				map.set(taskId, run);
			}
		}
		return map;
	}, [agentRuns]);

	// Loading state
	if (isLoadingAgent || isLoadingTasks) {
		return (
			<div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 max-w-6xl">
				<div className="space-y-4 sm:space-y-6">
					<Card className="p-6">
						<div className="flex items-start gap-4">
							<Skeleton className="h-16 w-16 rounded-full" />
							<div className="flex-1 space-y-2">
								<Skeleton className="h-8 w-48" />
								<Skeleton className="h-4 w-32" />
								<Skeleton className="h-4 w-24" />
							</div>
						</div>
					</Card>
					<div className="space-y-3">
						<Skeleton className="h-24 w-full" />
						<Skeleton className="h-24 w-full" />
						<Skeleton className="h-24 w-full" />
					</div>
				</div>
			</div>
		);
	}

	if (agentError) {
		return (
			<div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 max-w-6xl">
				<div className="flex items-center justify-center min-h-[400px]">
					<div className="text-center space-y-2">
						<h2 className="text-2xl font-semibold">Teammate not found</h2>
						<p className="text-muted-foreground">
							This teammate doesn't exist or you don't have access to it.
						</p>
					</div>
				</div>
			</div>
		);
	}

	if (!agent) return null;

	// ── Board callbacks (not modal-related) ──────────────────────────────────

	const handleRunTask = async (taskId: number) => {
		if (agent.status !== "enabled") {
			toast.error("Cannot run tasks for disabled or archived agents");
			return;
		}
		const task = allTasks?.find((t) => t.id === taskId);
		if (!task) {
			toast.error("Task not found");
			return;
		}
		if (task.status === "disabled") {
			toast.error("Cannot run paused tasks. Please resume the task first.");
			return;
		}
		if (task.status === "archived") {
			toast.error("Cannot run archived tasks");
			return;
		}

		const toastId = `run-task-${taskId}`;
		toast.loading("Starting task...", { id: toastId, description: task.name });
		runTask.mutate(taskId, {
			onSuccess: ({ runId }) => {
				toast.success("Task is running", {
					id: toastId,
					description: task.name,
					action: {
						label: "View run",
						onClick: () => navigate({ to: "/runs/$runId", params: { runId: String(runId) } }),
					},
					duration: 10000,
				});
			},
			onError: (error) => {
				toast.error("Failed to start task", { id: toastId, description: error.message });
			},
		});
	};

	const handlePlanTask = async (taskId: number) => {
		await handleMoveTaskStage(taskId, "planning");
		updateTaskById.mutate(
			{ taskId, updates: { permission_mode: "plan" } },
			{
				onSuccess: () => {
					runTask.mutate(taskId, {
						onSuccess: ({ runId }) => {
							toast.success("Planning started", {
								action: {
									label: "View run",
									onClick: () => navigate({ to: "/runs/$runId", params: { runId: String(runId) } }),
								},
								duration: 10000,
							});
						},
						onError: (error) => {
							toast.error("Failed to start planning", { description: error.message });
						},
					});
				},
				onError: (error) => {
					captureFrontendError(error, {
						area: "teammate_page",
						action: "plan_task_update_permission",
						taskId,
					});
					toast.error("Failed to set up planning mode", {
						description: error instanceof Error ? error.message : "Please try again.",
					});
				},
			}
		);
	};

	const handleApproveTask = async (taskId: number) => {
		await handleMoveTaskStage(taskId, "done");
		toast.success("Task approved");
	};

	const handleOpenRunPreview = (runId: number) => {
		openRunPreview(runId);
	};

	const handleMaximizeRun = (runId: number) => {
		closeRunPreview();
		navigate({ to: "/runs/$runId", params: { runId: String(runId) } });
	};

	const handleMoveTaskStage = async (taskId: number, stage: WorkflowStage) => {
		const task = allTasks?.find((t) => t.id === taskId);
		if (!task) return;
		if (task.status === "archived") {
			toast.error("Cannot move archived tasks");
			throw new Error("Task archived");
		}
		await updateTaskById.mutateAsync({
			taskId,
			updates: { workflow_stage: stage } as components["schemas"]["TaskUpdate"],
		});
	};

	const handleEnqueueTask = async (taskId: number) => {
		if (agent.status !== "enabled") {
			toast.error("Cannot enqueue tasks for disabled or archived agents");
			throw new Error("Agent not enabled");
		}
		const task = allTasks?.find((t) => t.id === taskId);
		if (!task) {
			toast.error("Task not found");
			throw new Error("Task not found");
		}
		if (task.status !== "enabled") {
			toast.error("Only active tasks can be moved to queue");
			throw new Error("Task not enabled");
		}

		const toastId = `enqueue-task-${taskId}`;
		toast.loading("Queueing task...", { id: toastId, description: task.name });
		try {
			const result = await enqueueTask.mutateAsync(taskId);
			toast.success("Task is running", {
				id: toastId,
				description: task.name,
				action: {
					label: "View run",
					onClick: () => navigate({ to: "/runs/$runId", params: { runId: String(result.run_id) } }),
				},
				duration: 10000,
			});
		} catch (error) {
			captureFrontendError(error, {
				area: "teammate_page",
				action: "enqueue_task",
				agentId,
				taskId,
			});
			toast.error("Failed to queue task", {
				id: toastId,
				description: error instanceof Error ? error.message : "Please try again.",
			});
			throw error;
		}
	};

	// ── Render ────────────────────────────────────────────────────────────────

	return (
		<div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 max-w-6xl">
			<div className="space-y-4 sm:space-y-6">
				<TeammateHeader agent={agent} taskCount={tasks.length} />

				<Card className="p-4 sm:p-6">
					<div className="space-y-4 sm:space-y-6">
						<TaskFilters
							onNewTask={taskModal.handleNewTask}
							disabled={agent.status !== "enabled"}
						/>
						{viewMode === "kanban" ? (
							<TaskBoard
								tasks={tasks}
								onSelect={setSelectedTask}
								onRunNow={handleRunTask}
								onPlan={handlePlanTask}
								onMoveStage={handleMoveTaskStage}
								onEnqueue={handleEnqueueTask}
								onApprove={handleApproveTask}
								onOpenRunPreview={handleOpenRunPreview}
								onNewTask={taskModal.handleNewTaskForStage}
								latestRunByTaskId={latestRunByTaskId}
								hideUnassigned={kanbanHideUnassigned}
								onToggleUnassigned={() => setKanbanHideUnassigned(!kanbanHideUnassigned)}
							/>
						) : (
							<TaskList
								tasks={tasks}
								onEdit={taskModal.handleEditTask}
								onSelect={setSelectedTask}
								onRunNow={handleRunTask}
								onSchedule={taskModal.handleScheduleTask}
								latestRunByTaskId={latestRunByTaskId}
							/>
						)}
					</div>
				</Card>
			</div>

			{/* Task creation/edit modal */}
			<TaskConfirmationModal
				open={taskModal.isModalOpen}
				onOpenChange={taskModal.setIsModalOpen}
				instructions={taskModal.taskInstructions}
				onSubmit={taskModal.handleSubmitTask}
				isSubmitting={taskModal.isSubmitting}
				isEditMode={taskModal.editingTaskId !== null}
				defaultName={taskModal.defaultName}
				defaultIntegrations={taskModal.defaultIntegrations}
				defaultGoals={taskModal.defaultGoals}
				showInstructionsField={true}
				defaultSchedule={taskModal.defaultSchedule}
				defaultWebhook={taskModal.defaultWebhook}
				defaultPermissionMode={taskModal.defaultPermissionMode}
				showPermissionModeSelector={taskModal.showPermissionModeSelector}
				requirePermissionModeSelection={taskModal.showPermissionModeSelector}
				hideScheduleSection={taskModal.hideScheduleSection}
				hideEmailInboxSection={true}
				submitLabel={taskModal.submitLabel}
				onRotateWebhook={taskModal.handleRotateWebhookFromModal}
			/>

			<Dialog
				open={taskModal.isWebhookDialogOpen}
				onOpenChange={(open) => {
					taskModal.setIsWebhookDialogOpen(open);
					if (!open) taskModal.setWebhookUrlDialog(null);
				}}
			>
				<DialogContent className="max-w-xl">
					<DialogHeader>
						<DialogTitle>Webhook URL Created</DialogTitle>
						<DialogDescription>
							Make a POST request to this URL with an optional JSON payload to trigger this task.
							The full URL is shown only once — copy it now.
						</DialogDescription>
					</DialogHeader>
					{taskModal.webhookUrlDialog && (
						<div className="rounded-md border bg-muted/40 p-3 flex items-start justify-between gap-3">
							<code className="text-xs break-all">{taskModal.webhookUrlDialog}</code>
							<CopyButton content={taskModal.webhookUrlDialog} variant="outline" size="sm" />
						</div>
					)}
					<DialogFooter>
						<Button type="button" onClick={() => taskModal.setIsWebhookDialogOpen(false)}>
							Close
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<TaskRunPanel
				open={isPanelOpen}
				onOpenChange={(open) => {
					if (!open) closePanel();
				}}
				task={allTasks?.find((t) => t.id === selectedTaskId) ?? null}
				onRunNow={handleRunTask}
				onEdit={taskModal.handleEditTask}
				onSchedule={taskModal.handleScheduleTask}
				latestRun={selectedTaskId ? latestRunByTaskId.get(selectedTaskId) : undefined}
				isRunning={runTask.isPending}
			/>

			<RunPreviewPanel
				runId={previewRunId}
				open={isRunPreviewOpen}
				onOpenChange={(open) => {
					if (!open) closeRunPreview();
				}}
				onMaximize={handleMaximizeRun}
			/>
		</div>
	);
}
